import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";

export const useLongPress = ({
  onLongPress,
  onClick,
  onContextMenu,
  timeout = 1000,
}) => {
  const refIsInvokedForLastInteraction = useRef(false);
  const refTimerId = useRef(undefined);
  const refProps = useRef({
    timeout,
    onLongPress,
    onClick,
    onContextMenu,
  });
  refProps.current = {
    timeout,
    onLongPress,
    onClick,
    onContextMenu,
  };
  const clearTimeoutLongPress = useCallback(() => {
    if (refTimerId.current !== undefined) {
      clearTimeout(refTimerId.current);
      refTimerId.current = undefined;
    }
  }, []);

  const handleStart = useCallback(
    (e) => {
      // if (!global.window?.[WINDOW_KEY_IS_LAST_POINTER_MOUSE] && e.type.indexOf("mouse") === 0) {
      //   return;
      // }
      refIsInvokedForLastInteraction.current = false;

      e.persist();

      clearTimeoutLongPress();

      refTimerId.current = setTimeout(() => {
        if (!refIsInvokedForLastInteraction.current) {
          if (typeof refProps.current.onLongPress === "function") {
            refProps.current.onLongPress(e);
            refIsInvokedForLastInteraction.current = true;
          }
        }

        clearTimeoutLongPress();
      }, refProps.current.timeout);
    },
    [clearTimeoutLongPress],
  );

  const handleEnd = useCallback((e) => {
    if (refIsInvokedForLastInteraction.current) {
      e.preventDefault();
    }
    clearTimeoutLongPress();
  }, [clearTimeoutLongPress]);

  const handleCtxMenu = useCallback((e) => {

    // prevent context menu, if long press was called for the current touch.
    if (!refIsInvokedForLastInteraction.current) {
      if (refProps.current.onContextMenu) {
        refProps.current.onContextMenu(e);
        refIsInvokedForLastInteraction.current = true;

        clearTimeoutLongPress();
      }
    }

    e.preventDefault();
  }, [clearTimeoutLongPress]);

  const handleClick = useCallback((e) => {
    // prevent click, if either ctx menu or long press was called for the current touch.
    if (!refIsInvokedForLastInteraction.current) {
      if (refProps.current.onClick) {
        refProps.current.onClick(e);
      }
    }

    clearTimeoutLongPress();
  }, [clearTimeoutLongPress]);

  useEffect(() => {
    return clearTimeoutLongPress;
  }, [clearTimeoutLongPress]);

  return {
    onTouchStart: handleStart,
    onMouseDown: handleStart,

    onMouseMove: clearTimeoutLongPress,
    onTouchMove: clearTimeoutLongPress,
    onTouchCancel: clearTimeoutLongPress,
    onMouseLeave: clearTimeoutLongPress,

    onTouchEnd: handleEnd,
    onMouseUp: handleEnd,

    onClick: handleClick,
    onContextMenu: handleCtxMenu,
  };
};

const LongPressWrapper = forwardRef(({
  children,
  onLongPress,
  onClick,
  onContextMenu,
  timeout,
  tag: Tag = "div",
  ...restProps
}, ref) => {
  const longPressListeners = useLongPress({
    onLongPress,
    onClick,
    onContextMenu,
    timeout,
  });
  return (
    <Tag {...restProps} {...longPressListeners} ref={ref}>
      {children}
    </Tag>
  );
});

LongPressWrapper.propTypes = {
  children: PropTypes.node,
  onLongPress: PropTypes.func,
  onClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  timeout: PropTypes.number,
  tag: PropTypes.string
};

export default LongPressWrapper;
